import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Coupon } from '@thurstech/commons/dist/entity/coupon';
import { BusinessDetail, WebsiteDetail, BankDetail } from '@thurstech/commons/dist/entity/prop';
import { PropBookingMeta, PropBookingMetaWithTransaction } from '@thurstech/commons/dist/models/booking';
import _ from 'lodash';

import { addLoadingReducers, BaseState, ThunkReducerMap } from '../common';
import {
  addCoupon,
  authProp,
  createProp,
  getBankDetail,
  getBooking,
  getBookings,
  getBookingsWithAllData,
  getBookingsWithTransaction,
  getBusinessDetail,
  getCoupons,
  getPresignedPost,
  getWebsiteDetail,
  impersonate,
  loginProp,
  logoutProp,
  propExist,
  propUpdatePassword,
  propVerifyForgotPasswordEmail,
  saveBankDetail,
  saveBusinessDetail,
  saveInstagramLogin,
  saveWebsiteDetail,
  submitQuery,
  updateCoupon,
  validateImpersonation
} from '../thunk/propThunk';

const propThunks = {
  validateImpersonationThunk: createAsyncThunk('prop/validateImpersonation', validateImpersonation),
  impersonateThunk: createAsyncThunk('prop/impersonate', impersonate),
  propExistThunk: createAsyncThunk('prop/exist', propExist),
  propVerifyForgotPasswordEmailThunk: createAsyncThunk(
    'prop/verifyForgotPasswordEmail',
    propVerifyForgotPasswordEmail
  ),
  loginPropThunk: createAsyncThunk('prop/loginProp', loginProp),
  createPropThunk: createAsyncThunk('prop/createProp', createProp),
  propUpdatePasswordThunk: createAsyncThunk('prop/updatePassword', propUpdatePassword),
  authPropThunk: createAsyncThunk('prop/authProp', authProp),
  logoutPropThunk: createAsyncThunk('prop/logoutProp', logoutProp),
  submitQueryThunk: createAsyncThunk('prop/submitQuery', submitQuery),
  getPresignedPostThunk: createAsyncThunk('prop/getPresignedPost', getPresignedPost),
  saveBusinessDetailThunk: createAsyncThunk('prop/saveBusinessDetail', saveBusinessDetail),
  getBusinessDetailThunk: createAsyncThunk('prop/getBusinessDetail', getBusinessDetail),
  saveInstagramLoginThunk: createAsyncThunk('prop/saveInstagramLogin', saveInstagramLogin),
  saveBankDetailThunk: createAsyncThunk('prop/saveBankDetail', saveBankDetail),
  getBankDetailThunk: createAsyncThunk('prop/getBankDetail', getBankDetail),
  saveWebsiteDetailThunk: createAsyncThunk('prop/saveWebsiteDetail', saveWebsiteDetail),
  getWebsiteDetailThunk: createAsyncThunk('prop/getWebsiteDetail', getWebsiteDetail),
  addCouponThunk: createAsyncThunk('prop/addCoupon', addCoupon),
  updateCouponThunk: createAsyncThunk('prop/updateCoupon', updateCoupon),
  getCouponsThunk: createAsyncThunk('prop/getCoupons', getCoupons),
  getBookingsThunk: createAsyncThunk('prop/getBookings', getBookings),
  getBookingsWithAllDataThunk: createAsyncThunk('prop/getBookingsWithAllData', getBookingsWithAllData),
  getBookingThunk: createAsyncThunk('prop/getBooking', getBooking),
  getBookingsWithTransactionThunk: createAsyncThunk(
    'prop/getBookingsWithTransaction',
    getBookingsWithTransaction
  )
};
export const {
  validateImpersonationThunk,
  impersonateThunk,
  propExistThunk,
  loginPropThunk,
  createPropThunk,
  propUpdatePasswordThunk,
  propVerifyForgotPasswordEmailThunk,
  authPropThunk,
  logoutPropThunk,
  submitQueryThunk,
  getPresignedPostThunk,
  saveBusinessDetailThunk,
  getBusinessDetailThunk,
  saveWebsiteDetailThunk,
  getWebsiteDetailThunk,
  saveBankDetailThunk,
  saveInstagramLoginThunk,
  addCouponThunk,
  updateCouponThunk,
  getCouponsThunk,
  getBankDetailThunk,
  getBookingsThunk,
  getBookingsWithAllDataThunk,
  getBookingThunk,
  getBookingsWithTransactionThunk
} = propThunks;

interface PropState extends BaseState {
  propId?: string;
  businessDetail?: BusinessDetail;
  websiteDetail?: WebsiteDetail;
  bankDetail?: BankDetail;
  coupons?: Coupon[];
  bookingMetas?: PropBookingMeta[];
  bookingsWithTransaction?: PropBookingMetaWithTransaction[];
}

const initialState: PropState = {
  isLoading: { [authPropThunk.pending.toString()]: true }, //Defailting this value so that the loading symbol apears when a protectedLayout is opened
  error: {},
  propId: undefined
};
export const propSlice = createSlice({
  name: 'prop',
  initialState,
  reducers: {
    setGeneratedDomain: (state, action) => {
      state.businessDetail = {
        ...((state.businessDetail ?? {}) as BusinessDetail),
        generatedDomain: action.payload as string
      };
    },
    setInstagramLogin: (state, action: { payload: void }) => {
      state.websiteDetail = { ...state.websiteDetail, isInstagramConnected: true } as WebsiteDetail;
    }
  },
  extraReducers: builder => {
    addLoadingReducers(
      builder,
      propThunks,
      new ThunkReducerMap<PropState, typeof propThunks>()
        .addCase(loginPropThunk.fulfilled, (state, action) => {
          state.propId = action.payload?.id;
        })
        .addCase(createPropThunk.fulfilled, (state, action) => {
          state.propId = action.payload?.id;
        })
        .addCase(propUpdatePasswordThunk.fulfilled, (state, action) => {
          state.propId = action.payload?.id;
        })
        .addCase(authPropThunk.fulfilled, (state, action) => {
          state.propId = action.payload?.id;
        })
        .addCase(logoutPropThunk.fulfilled, state => {
          _.assign(state, _.cloneDeep(initialState));
          delete state.isLoading[authPropThunk.pending.toString()];
        })
        .addCase(getBusinessDetailThunk.fulfilled, (state, action) => {
          state.businessDetail = action.payload;
        })
        .addCase(getWebsiteDetailThunk.fulfilled, (state, action) => {
          state.websiteDetail = _.isEmpty(action.payload) ? undefined : action.payload;
        })
        .addCase(getBankDetailThunk.fulfilled, (state, action) => {
          state.bankDetail = action.payload;
        })
        .addCase(addCouponThunk.fulfilled, (state, action) => {
          const coupon = {
            ...action.meta.arg,
            id: action.payload.id,
            propId: state.propId!
          } as Coupon;
          state.coupons?.push(coupon);
        })
        .addCase(updateCouponThunk.fulfilled, (state, action) => {
          const couponInd = state.coupons?.findIndex(coup => coup.id === action.meta.arg.id);
          if (couponInd !== undefined && couponInd !== -1) {
            const coupon = state.coupons!.at(couponInd)!;
            state.coupons![couponInd] = { ...coupon, ..._.omit(action.meta.arg, 'type') };
          }
        })
        .addCase(getCouponsThunk.fulfilled, (state, action) => {
          state.coupons = action.payload.coupons;
        })
        .addCase(getBookingsThunk.pending, state => {
          state.bookingMetas = undefined;
        })
        .addCase(getBookingsThunk.fulfilled, (state, action) => {
          state.bookingMetas = action.payload.bookings;
        })
        .addCase(getBookingsWithTransactionThunk.fulfilled, (state, action) => {
          state.bookingsWithTransaction = action.payload.bookings;
        })
    );
  }
});

export const { setGeneratedDomain, setInstagramLogin } = propSlice.actions;
